import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
// Highcharts
import * as Highcharts from 'highcharts';
// Models
import { Vehicle, BatteryLogbook } from '@mocca-technologies/model';
import { selectedValues } from '../../logbooks-menu/logbooks-menu.component';
import { TimeFilter } from '../../logbooks-time-filter/logbooks-time-filter.component';
// Services
import { LogbookService } from 'src/app/services/logbooks.service';
import { CoreService } from 'src/app/services/core.service';
// COmponents
import { QRCodeComponent } from 'angularx-qrcode';
// HTML to canvas
import html2canvas from 'html2canvas';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-batteries-summary',
  templateUrl: './batteries-summary.component.html',
  styleUrls: ['./batteries-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class BatteriesSummaryComponent implements OnInit {

  /**
   * Output variables
   */

  @Output() timeFilterChange = new EventEmitter<TimeFilter>();
  @Output() selectedVehicleFromGraph = new EventEmitter<string>();

  /**
   * Input variables
   */

  @Input() batteryLogbook: BatteryLogbook;
  @Input() selectedValues: selectedValues;

  @Input() vehicleList: Vehicle[] = [];

  /**
   * Child variables
   */

  @ViewChild('qrcode', { static: false }) qrcode: QRCodeComponent;
  @ViewChild('qrCodeContainer', { static: false }) qrCodeContainer: ElementRef;

  /**
   * Variables
   */

  selectedBattery: string = "Unkown"; // Change to Battery model
  chartType: 'capacity' | 'discharge' = 'capacity';

  droneUsageChart: Highcharts.Chart;
  capacityChart: Highcharts.Chart;

  qrCodeUrl: string;
  qrWidth: number = 256;
  qrErrorCorrectionLevel: string = 'M';

  popoverOptions = {
    cssClass: 'popover-style'
  };

  today: Date = new Date();

  user = JSON.parse(localStorage.getItem('user'));

  qrCodeWidth: number = 300;

  constructor(
    public logbookService: LogbookService,
    private coreService: CoreService,
    private translateService: TranslateService
  ) {}

  /**
   * Gets the primary color from CSS variable
   */
  private getPrimaryColor(): string {
    const element = document.documentElement;
    const primaryColor = getComputedStyle(element).getPropertyValue('--ion-color-primary').trim();
    return primaryColor || '#0066FF';  // Fallback color
  }

  /**
   * Generates a color palette derived from primary color
   */
  private generateColorPalette(): string[] {
    const primaryColor = this.getPrimaryColor();
    const hslColor = this.hexToHsl(primaryColor);
    const palette: string[] = [];

    // Generate variations of the primary color
    for (let i = 0; i < 10; i++) {
      const lightness = 30 + (i * 5); // Vary lightness from 30 to 75
      palette.push(`hsl(${hslColor.h}, ${hslColor.s}%, ${lightness}%)`);
    }
    return palette;
  }

  /**
   * Converts hex color to HSL
   */
  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }


  // #region --> Lifecycle Methods

  ngOnInit() {

    this.qrCodeUrl = 'https://mocca-technologies.com/battery-charging-register/' + this.selectedBattery;
    this.initializeCharts();
    this.translateService.onLangChange.subscribe(() => {
      if (this.droneUsageChart || this.capacityChart) {
        this.updateChartTitles();
      }
    });

  }

  // #endregion --> Lifecycle Methods

  // #region --> Chart methods

  /**
   * This method is used to switch between the last capacity and discharge charts. It checks the current chart type and calls the appropriate method to render the chart.
   */
  switchChart() {
    if (this.chartType === 'capacity') {
      this.renderCapacityChart();
    } else {
      this.renderDischargeChart();
    }
  }

  /**
   * This method is used to create the tooltip formatter for the charts. It returns a function that formats the tooltip content.
   * @returns
   */
  private createTooltipFormatter() {
    const translateService = this.translateService; // Capture the translateService in a local variable
    return function() {
      return `<b>${this.point.name}</b><br/>
              <span style="color:${this.point.color}">${(this.point.options as any).description || ''}</span><br/>
              ${translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.OPERATIONS')}: <b>${this.y}</b> (${Highcharts.numberFormat(this.percentage, 1)}%)`;
    };
  }

  /**
   * This method is used to render the last capacity count chart. It calculates the number of operations for each last capacity range (low, medium, high) and updates the chart with the new data.
   */
  renderCapacityChart() {

    const capacityRanges = {
      'Low': 0,
      'Medium': 0,
      'High': 0
    };

    this.batteryLogbook?.batteryLogTable?.forEach(entry => {
      const capacity = entry.landingCapacity;
      if (capacity <= 30) {
        capacityRanges['Low']++;
      } else if (capacity <= 60) {
        capacityRanges['Medium']++;
      } else {
        capacityRanges['High']++;
      }
    });

    const capacityData = [
      {
        name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LOW'),
        y: capacityRanges['Low'],
        color: '#dc3545',
        description: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LOW_DESCRIPTION')
      },
      {
        name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.MEDIUM'),
        y: capacityRanges['Medium'],
        color: '#fd7e14',
        description: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.MEDIUM_DESCRIPTION')
      },
      {
        name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.HIGH'),
        y: capacityRanges['High'],
        color: '#28a745',
        description: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.HIGH_DESCRIPTION')
      }
    ].filter(item => item.y > 0);

    if (this.capacityChart) {
      this.capacityChart.update({
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LAST_CAPACITY_DISTRIBUTION'),
          style: { color: 'var(--ion-color-primary)', fontWeight: 'normal' }
        },
        tooltip: {
          formatter: this.createTooltipFormatter()
        },
        series: [{
          type: 'pie',
          name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LAST_CAPACITY'),
          data: capacityData
        }]
      });
    } else {
      this.capacityChart = Highcharts.chart('capacityChartContainer',
        {
          chart: {
            type: 'pie'
          },
          title: {
            text: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LAST_CAPCITY_DISTRIBUTION'),
            style: { color: 'var(--ion-color-primary)', fontWeight: 'normal' }
          },
          tooltip: {
            formatter: this.createTooltipFormatter()
          },
          series: [{
            type: 'pie',
            name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LAST_CAPACITY'),
            data: capacityData,

          }],
          credits: {
            enabled: false
          },
          responsive: {
            rules: [
              {
                condition: {
                  maxWidth: 100,
                  maxHeight: 120
                },
                chartOptions: {
                  chart: {
                    spacing: [5, 5, 5, 5],
                  },
                  title: {
                    style: { fontSize: '3px' },
                    y: 20,
                    margin: 0
                  },
                  legend: {
                    itemStyle: { fontSize: '5px' },
                    itemDistance: 2,
                    layout: 'vertical',
                    align: 'center',
                    verticalAlign: 'bottom'
                  },
                  plotOptions: {
                    pie: {
                      size: '85%',
                      dataLabels: {
                        enabled: false,
                      }
                    }
                  }
                }
              },
              // Small screens
              {
                condition: {
                  minWidth: 100,
                  minHeight: 120,
                  maxWidth: 173,
                  maxHeight: 179
                },
                chartOptions: {
                  chart: {
                    spacing: [5, 5, 5, 5],
                  },
                  title: {
                    style: { fontSize: '6px' },
                    y: 20,
                    margin: 0
                  },
                  legend: {
                    itemStyle: { fontSize: '10px' },
                    itemDistance: 2,
                    layout: 'vertical',
                    align: 'center',
                    verticalAlign: 'bottom'
                  },
                  plotOptions: {
                    pie: {
                      size: '85%',
                      dataLabels: {
                        enabled: false,
                      }
                    }
                  }
                }
              },
              // Medium screens
              {
                condition: {
                  minWidth: 163,
                  minHeight: 180,
                },
                chartOptions: {
                  chart: {
                    spacing: [5, 5, 5, 5]
                  },
                  title: {
                    style: { fontSize: '8px' }
                  },
                  legend: {
                    itemStyle: { fontSize: '12px' },
                    itemDistance: 2
                  },
                  plotOptions: {
                    pie: {
                      size: '88%',
                      dataLabels: {
                        style: { fontSize: '10px' },
                        distance: 2
                      }
                    }
                  }
                }
              },
              // Large screens
              {
                condition: {
                  minWidth: 220,
                  minHeight: 198
                },
                chartOptions: {
                  chart: {
                    spacing: [5, 5, 5, 5]
                  },
                  title: {
                    style: { fontSize: '14px' }
                  },
                  legend: {
                    itemStyle: { fontSize: '14px' },
                    itemDistance: 2
                  },
                  plotOptions: {
                    pie: {
                      size: '90%',
                      dataLabels: {
                        style: { fontSize: '12px' },
                        distance: 2
                      }
                    }
                  }
                }
              }
            ]
          }
        }
      );
    }
  }

  /**
   * This method is used to render the discharge chart. It calculates the number of operations for each discharge range (low, medium, high) and updates the chart with the new data.
   */
  renderDischargeChart() {

    const dischargeRanges = {
      'Low': 0,
      'Medium': 0,
      'High': 0
    };

    this.batteryLogbook?.batteryLogTable?.forEach(entry => {
      const discharge = entry.takeOffCapacity - entry.landingCapacity;

      if (discharge <= 30) {
        dischargeRanges['Low']++;
      } else if (discharge <= 60) {
        dischargeRanges['Medium']++;
      } else {
        dischargeRanges['High']++;
      }
    });

    const dischargeData = [
      {
        name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LOW'),
        y: dischargeRanges['Low'],
        color: '#28a745',
        description: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LOW_DISCHARGE')
      },
      {
        name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.MEDIUM'),
        y: dischargeRanges['Medium'],
        color: '#fd7e14',
        description: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.MEDIUM_DISCHARGE')
      },
      {
        name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.HIGH'),
        y: dischargeRanges['High'],
        color: '#dc3545',
        description: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.HIGH_DISCHARGE')
      }
    ].filter(item => item.y > 0);

    this.capacityChart.update({
      title: {
        text: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.DISCHARGE_BY_OPERATION'),
        style: { color: 'var(--ion-color-primary)', fontWeight: 'normal' }
      },
      tooltip: {
        formatter: this.createTooltipFormatter()
      },
      series: [{
        type: 'pie',
        name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.DISCHARGE'),
        data: dischargeData
      }],
      credits: {
        enabled: false
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 100,
              maxHeight: 120
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 5],
              },
              title: {
                style: { fontSize: '6px' },
                y: 20,
                margin: 0
              },
              legend: {
                itemStyle: { fontSize: '5px' },
                itemDistance: 2,
                layout: 'vertical',
                align: 'center',
                verticalAlign: 'bottom'
              },
              plotOptions: {
                pie: {
                  size: '70%',
                  dataLabels: {
                    enabled: false,
                  }
                }
              }
            }
          },
          // Small screens
          {
            condition: {
              minWidth: 100,
              minHeight: 120,
              maxWidth: 173,
              maxHeight: 179
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 5],
              },
              title: {
                style: { fontSize: '12px' },
                y: 20,
                margin: 0
              },
              legend: {
                itemStyle: { fontSize: '10px' },
                itemDistance: 2,
                layout: 'vertical',
                align: 'center',
                verticalAlign: 'bottom'
              },
              plotOptions: {
                pie: {
                  size: '70%',
                  dataLabels: {
                    enabled: false,
                  }
                }
              }
            }
          },
          // Medium screens
          {
            condition: {
              minWidth: 163,
              minHeight: 180,
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 5]
              },
              title: {
                style: { fontSize: '14px' }
              },
              legend: {
                itemStyle: { fontSize: '12px' },
                itemDistance: 2
              },
              plotOptions: {
                pie: {
                  dataLabels: {
                    style: { fontSize: '10px' },
                    distance: 2
                  }
                }
              }
            }
          },
          // Large screens
          {
            condition: {
              minWidth: 220,
              minHeight: 198
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 5]
              },
              title: {
                style: { fontSize: '18px' }
              },
              legend: {
                itemStyle: { fontSize: '14px' },
                itemDistance: 2
              },
              plotOptions: {
                pie: {
                  dataLabels: {
                    style: { fontSize: '12px' },
                    distance: 2
                  }
                }
              }
            }
          }
        ]
      }
    });
  }

  /**
   * This method is used to initialize the drone usage chart and the battery degradation chart. It defines the type, title and data of the charts.
   */
  initializeCharts() {

    this.droneUsageChart = Highcharts.chart('droneUsageChartContainer',
      {
        colors: this.generateColorPalette(),
        chart: {
          type: 'pie'
        },
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.BATTERY_CONSUMPTION'),
          style: { color: 'var(--ion-color-primary)', fontWeight: 'normal' }
        },
        tooltip: {
          formatter: this.createTooltipFormatter()
        },
        series: [{
          type: 'pie',
          name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.BATTERY_USAGE'),
          data: this.batteryLogbook?.batteryUsageList?.map(battery => ({
            name: battery?.vehicle?.plate,
            y: battery?.numOperations
          }))
        }],
        credits: {
          enabled: false
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            point: {
              events: {
                click: (event) => {
                  this.selectVehicle(event.point.name);
                }
              }
            },
          }
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 100,
                maxHeight: 120
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5],
                },
                title: {
                  style: { fontSize: '6px' },
                  y: 20,
                  margin: 0
                },
                legend: {
                  itemStyle: { fontSize: '5px' },
                  itemDistance: 2,
                  layout: 'vertical',
                  align: 'center',
                  verticalAlign: 'bottom'
                },
                plotOptions: {
                  pie: {
                    size: '70%',
                    dataLabels: {
                      enabled: false,
                    }
                  }
                }
              }
            },
            // Small screens
            {
              condition: {
                minWidth: 100,
                minHeight: 120,
                maxWidth: 173,
                maxHeight: 180
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5],
                },
                title: {
                  style: { fontSize: '12px' },
                  y: 20,
                  margin: 0
                },
                legend: {
                  itemStyle: { fontSize: '10px' },
                  itemDistance: 2,
                  layout: 'vertical',
                  align: 'center',
                  verticalAlign: 'bottom'
                },
                plotOptions: {
                  pie: {
                    size: '70%',
                    dataLabels: {
                      enabled: false,
                    }
                  }
                }
              }
            },
            // Medium screens
            {
              condition: {
                minWidth: 163,
                minHeight: 180,
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5]
                },
                title: {
                  style: { fontSize: '14px' }
                },
                legend: {
                  itemStyle: { fontSize: '12px' },
                  itemDistance: 2
                },
                plotOptions: {
                  pie: {
                    dataLabels: {
                      style: { fontSize: '10px' },
                      distance: 2
                    }
                  }
                }
              }
            },
            // Large screens
            {
              condition: {
                minWidth: 220,
                minHeight: 198
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5]
                },
                title: {
                  style: { fontSize: '18px' }
                },
                legend: {
                  itemStyle: { fontSize: '14px' },
                  itemDistance: 2
                },
                plotOptions: {
                  pie: {
                    dataLabels: {
                      style: { fontSize: '12px' },
                      distance: 2
                    }
                  }
                }
              }
            }
          ]
        }
      }
    );

    this.switchChart();
  }

  /**
   * This method is used to update the data in the charts. It updates the data of the drone usage chart and the battery degradation chart.
   */
  updateCharts() {

    if (this.droneUsageChart) {

      this.droneUsageChart.update({
        series: [{
          type: 'pie',
          name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.BATTERY_USAGE'),
          data: this.batteryLogbook?.batteryUsageList?.map(battery => ({
            name: battery?.vehicle?.plate,
            y: battery?.numOperations
          }))
        }]
      });

    }

    if (this.capacityChart) {

      this.switchChart();

    }
  }

  selectVehicle(plate: string) {

    if (plate && this.checkDroneBelongsToUser(plate)) {
      this.selectedVehicleFromGraph.emit(plate);
    } else {
      this.coreService.showServerResponse('400', this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.BAD_REQUEST'));
    }
  }

  checkDroneBelongsToUser(plate: string): boolean {
    let belongs = false;
    if (this.vehicleList && this.vehicleList.length > 0) {
      const vehicle = this.vehicleList.find(v => v.plate === plate);
      if (vehicle) {
        belongs = true;
      }
    }
    return belongs;
  }

  /**
   * Updates the chart titles when language changes
   */
  private updateChartTitles(): void {
    if (this.droneUsageChart) {
      this.droneUsageChart.update({
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.BATTERY_CONSUMPTION')
        },
        series: [{
          name: this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.BATTERY_USAGE')
        }]
      } as any, false);
      this.droneUsageChart.redraw();
    }

    if (this.capacityChart) {
      let chartTitle = '';
      let seriesName = '';

      if (this.chartType === 'capacity') {
        chartTitle = this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LAST_CAPACITY_DISTRIBUTION');
        seriesName = this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.LAST_CAPACITY');
      } else {
        chartTitle = this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.DISCHARGE_BY_OPERATION');
        seriesName = this.translateService.instant('PAGES.LOGBOOKS.BATTERY_LOGBOOK.DISCHARGE');
      }

      this.capacityChart.update({
        title: {
          text: chartTitle
        },
        series: [{
          name: seriesName
        }]
      } as any, false);
      this.capacityChart.redraw();
    }
  }

  // #endregion --> Chart methods

  // #region --> QR Methods

  /**
   * This method is used to update the QR code width when the user changes the value in the input field.
   */
  qrCodeWidthToCm(width: number): string {
    const cmWidth = (width / 96) * 2.54;
    return cmWidth.toFixed(2);
  }

  pinFormatter = (value: number): string => {
    return `${this.qrCodeWidthToCm(value)} cm`;
  }

  /**
   * This method is used to download the QR code of the selected battery as a PNG file.
   */
  downloadQRCode() {

    const qrCodeContainer = this.qrCodeContainer.nativeElement;
    qrCodeContainer.style.transform = `scale(${this.qrCodeWidth / 300})`;
    qrCodeContainer.style.transformOrigin = 'top left';

    html2canvas(qrCodeContainer).then(canvas => {
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = this.selectedBattery + '-qr-code.png';
      link.click();
      qrCodeContainer.style.transform = '';
    });
  }

  /**
   * This method is used to print the QR code of the selected battery.
   */
  printQRCode() {

    !environment.production && console.log(this.qrCodeWidth);
    const qrCodeContainer = this.qrCodeContainer.nativeElement;
    qrCodeContainer.style.transform = `scale(${this.qrCodeWidth / 300})`;
    qrCodeContainer.style.transformOrigin = 'top left';

    html2canvas(qrCodeContainer).then(canvas => {
      const dataUrl = canvas.toDataURL('image/png');
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);
      iframe.contentWindow.document.open();
      iframe.contentWindow.document.write(`
        <html>
          <head>
            <title>Print QR Code</title>
            <style>
              body, html {
                width: 100%;
                height: 100%;
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
              }
              #qrCodeImage {
                max-width: ${this.qrCodeWidth}px;
                width: 100%;
              }
            </style>
          </head>
          <body>
            <img id="qrCodeImage" src="${dataUrl}">
          </body>
        </html>
      `);
      iframe.contentWindow.document.close();

      const qrCodeImage = iframe.contentWindow.document.getElementById('qrCodeImage');
      qrCodeImage.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        document.body.removeChild(iframe);
        qrCodeContainer.style.transform = ''; // Reset the transform after printing
      };
    });
  }

  // #endregion --> QR Methods

  // #region --> Time Filter Methods

  /**
   * This method is used to update the time filter.
   * @param timeFilter
   */

  updateTimeFilter(timeFilter: TimeFilter) {
    this.timeFilterChange.emit(timeFilter);
  }

  // #endregion --> Time Filter Methods

  // #region --> Page Methods

  /**
   * This method checks if the battery logbook has summary data.
   * @returns
   */
  summaryData(): boolean {
    let summaryData = false;

    if ( this.batteryLogbook?.batteryLogTable.length > 0) {
      summaryData = true;
    }

    return summaryData;

  }

  // #endregion --> Page Methods
}
